/**
 * DayDetailDrawer — modal showing who's off and who's available on a
 * specific day, opened by clicking a cell in the Équipe → Congés calendar.
 *
 * Data flow:
 *   - allLeaves: every leave request the admin already fetched for the
 *     year (allYear from EquipeCongesTab). We filter to those whose
 *     expanded weekday list contains the clicked date.
 *   - configs: the team-wide config snapshot (also already fetched).
 *     Used as the canonical "list of team members" so we can compute
 *     who is available = anyone in configs not in the off-list.
 *   - holidays: the in-month holiday list. If the clicked date is a
 *     legal holiday we show that fact at the top.
 *
 * Independent vs employee status is shown next to each name so the
 * admin knows at a glance whose recup balance is impacted.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  XMarkIcon,
  CheckCircleIcon,
  SunIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import {
  leaveAPI,
  type LeaveRequestEnriched,
  type LeaveType,
  type PublicHoliday,
  type UserLeaveConfigEntry,
} from '@/features/admin/tabs/mon-espace/api/leave.api';
import { expandLeaveRequest, workingDowSet } from '@/features/admin/tabs/mon-espace/components/LeaveCalendar/expandRequest';
import './DayDetailDrawer.css';

const TYPE_LABEL: Record<LeaveType, string> = {
  legal: 'Congé légal',
  recup: 'Récupération',
  unpaid: 'Sans solde',
  sick: 'Maladie',
};

const STATUS_LABEL = {
  pending: 'En attente',
  approved: 'Approuvé',
} as const;

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

function userFullName(p: { first_name?: string | null; last_name?: string | null; user_email?: string | null; email?: string | null }) {
  const n = `${p.first_name || ''} ${p.last_name || ''}`.trim();
  return n || p.user_email || p.email || '?';
}

function formatDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  return `${DAY_NAMES[dow]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  return dow === 0 || dow === 6;
}

interface OffEntry {
  user_id: string;
  name: string;
  type: LeaveType;
  status: 'pending' | 'approved';
  fraction: number;
  employment_type?: 'employee' | 'independent';
}

interface DayDetailDrawerProps {
  dateIso: string;
  allLeaves: LeaveRequestEnriched[];
  configs: UserLeaveConfigEntry[];
  holidays: PublicHoliday[];
  onClose: () => void;
}

export const DayDetailDrawer: React.FC<DayDetailDrawerProps> = ({
  dateIso, allLeaves, configs, holidays, onClose,
}) => {
  const holiday = holidays.find(h => h.date === dateIso);
  const weekend = isWeekend(dateIso);

  // Team scheduled hours for this date (from planning template + overrides).
  // We display alongside each user so the admin can quickly tell who is
  // planned for how many hours, and what the team total is.
  const { data: teamHours = [] } = useQuery({
    queryKey: ['leave', 'team-day-hours', dateIso],
    queryFn: () => leaveAPI.teamDayHours(dateIso),
    staleTime: 60_000,
  });
  const hoursByUser = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of teamHours) m.set(h.user_id, h.scheduled_hours);
    return m;
  }, [teamHours]);

  // Build the off-list by expanding every leave request and filtering to
  // dateIso. Holidays + per-user working days are excluded like the backend —
  // someone whose leave range spans a férié isn't "en congé" on the férié.
  const offEntries = useMemo<OffEntry[]>(() => {
    const holidaySet = new Set(holidays.map(h => h.date));
    const out: OffEntry[] = [];
    for (const req of allLeaves) {
      if (req.status !== 'approved' && req.status !== 'pending') continue;
      const cfg = configs.find(c => c.user_id === req.user_id);
      const days = expandLeaveRequest(req, { holidays: holidaySet, workingDays: workingDowSet(cfg?.working_days) });
      const hit = days.find(d => d.date === dateIso);
      if (!hit) continue;
      out.push({
        user_id: req.user_id,
        name: userFullName({ first_name: req.first_name, last_name: req.last_name, user_email: req.user_email }),
        type: req.type,
        status: req.status as 'pending' | 'approved',
        fraction: hit.fraction,
        employment_type: cfg?.employment_type,
      });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [allLeaves, dateIso, configs, holidays]);

  // Available list = everyone in configs minus those in offEntries
  const available = useMemo(() => {
    const offIds = new Set(offEntries.map(o => o.user_id));
    return configs
      .filter(c => !offIds.has(c.user_id))
      .map(c => ({
        user_id: c.user_id,
        name: userFullName(c),
        employment_type: c.employment_type,
        role: c.role,
        scheduled_hours: hoursByUser.get(c.user_id) || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [configs, offEntries, hoursByUser]);

  // Aggregate scheduled hours actually worked: for each available user the
  // full scheduled value; for each off user the scheduled × (1 - fraction)
  // since half-day leaves leave the other half still worked.
  const totalScheduled = useMemo(
    () => Math.round(available.reduce((s, u) => s + u.scheduled_hours, 0) * 10) / 10,
    [available]
  );
  const totalLost = useMemo(
    () => Math.round(offEntries.reduce((s, o) => s + (hoursByUser.get(o.user_id) || 0) * o.fraction, 0) * 10) / 10,
    [offEntries, hoursByUser]
  );

  return (
    <div className="day-detail-backdrop" onClick={onClose}>
      <div className="day-detail-drawer" onClick={e => e.stopPropagation()} role="dialog">
        <header className="day-detail-header">
          <div>
            <h3 className="day-detail-title">{formatDayLabel(dateIso)}</h3>
            <p className="day-detail-subtitle">
              {holiday
                ? <><CalendarDaysIcon className="w-4 h-4" /> Férié · {holiday.label}</>
                : weekend
                  ? 'Week-end'
                  : (
                    <>
                      {offEntries.length} absent{offEntries.length > 1 ? 's' : ''}
                      {' · '}
                      {available.length} disponible{available.length > 1 ? 's' : ''}
                      {' · '}
                      <strong>{totalScheduled.toFixed(1)}h prévues</strong>
                      {totalLost > 0 && <span className="day-detail-lost"> (−{totalLost.toFixed(1)}h)</span>}
                    </>
                  )}
            </p>
          </div>
          <button className="day-detail-close" onClick={onClose} aria-label="Fermer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="day-detail-body">
          {/* Absents */}
          <section className="day-detail-section">
            <h4 className="day-detail-section-title">
              <SunIcon className="w-4 h-4" /> Absents ({offEntries.length})
            </h4>
            {offEntries.length === 0 ? (
              <div className="day-detail-empty">Personne absent ce jour.</div>
            ) : (
              <ul className="day-detail-list">
                {offEntries.map(o => {
                  const scheduled = hoursByUser.get(o.user_id) || 0;
                  const lost = scheduled * o.fraction;
                  return (
                  <li key={`${o.user_id}-${o.type}`} className="day-detail-row day-detail-row--off">
                    <div className="day-detail-row-main">
                      <span className="day-detail-row-name">{o.name}</span>
                      {o.employment_type && (
                        <span className={`day-detail-emp day-detail-emp--${o.employment_type}`}>
                          {o.employment_type === 'employee' ? 'Employé' : 'Indép.'}
                        </span>
                      )}
                    </div>
                    <div className="day-detail-row-side">
                      {scheduled > 0 && (
                        <span className="day-detail-hours day-detail-hours--off" title={`Prévu ${scheduled}h — absent ${lost.toFixed(1)}h`}>
                          −{lost.toFixed(1)}h
                        </span>
                      )}
                      <span className={`day-detail-type day-detail-type--${o.type}`}>{TYPE_LABEL[o.type]}</span>
                      {o.fraction < 1 && <span className="day-detail-half">½ jour</span>}
                      {o.status === 'pending' && <span className="day-detail-status-pending">{STATUS_LABEL.pending}</span>}
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Disponibles */}
          <section className="day-detail-section">
            <h4 className="day-detail-section-title">
              <CheckCircleIcon className="w-4 h-4" /> Disponibles ({available.length})
            </h4>
            {available.length === 0 ? (
              <div className="day-detail-empty">Personne dispo.</div>
            ) : (
              <ul className="day-detail-list">
                {available.map(u => (
                  <li key={u.user_id} className="day-detail-row day-detail-row--available">
                    <div className="day-detail-row-main">
                      <span className="day-detail-row-name">{u.name}</span>
                      {u.employment_type && (
                        <span className={`day-detail-emp day-detail-emp--${u.employment_type}`}>
                          {u.employment_type === 'employee' ? 'Employé' : 'Indép.'}
                        </span>
                      )}
                    </div>
                    <div className="day-detail-row-side">
                      {u.scheduled_hours > 0 ? (
                        <span className="day-detail-hours day-detail-hours--avail">{u.scheduled_hours.toFixed(1)}h</span>
                      ) : (
                        <span className="day-detail-hours-empty" title="Pas de planning encodé pour ce jour">—</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DayDetailDrawer;
